import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarPlus, BookOpen, Package, ShoppingBag, UserCircle,
  ClipboardList, Sparkles, Calendar, LogOut, LayoutDashboard,
  Settings as SettingsIcon,
} from 'lucide-react';
import { AuthContext } from '../../AuthContext';
import NotificationsBell from '../NotificationsBell';

// Client desktop chrome — mirror of ProviderShell, with client-side
// nav items instead of provider ones. Fixed 256px left sidebar +
// children rendered in the main column. Renders only at lg+. On <lg,
// Header.js + MobileMenu handle chrome.
//
// Usage in App.js:
//   <ClientShell active="my-bookings"><BookingList /></ClientShell>
//
// The `active` prop is a stable section key (not the URL) so the
// highlight survives query params and sub-routes.

const NAV_GROUPS = [
  {
    label: null,
    items: [
      { key: 'dashboard',    href: '/',             label: 'Dashboard',      icon: LayoutDashboard },
      { key: 'book',         href: '/book',         label: 'Book a session', icon: CalendarPlus },
      { key: 'my-bookings',  href: '/my-bookings',  label: 'My appointments', icon: BookOpen },
    ],
  },
  {
    label: 'Your account',
    items: [
      { key: 'my-packages',     href: '/my-packages',         label: 'My packages',       icon: Package },
      { key: 'my-membership',   href: '/my-membership',       label: 'Membership',        icon: Sparkles },
      { key: 'buy-packages',    href: '/packages',            label: 'Buy packages',      icon: ShoppingBag },
      { key: 'provider-calendar', href: '/availability',      label: "Provider's calendar", icon: Calendar },
    ],
  },
  {
    label: 'Profile',
    items: [
      { key: 'profile',     href: '/my-profile',         label: 'Profile',     icon: UserCircle },
      { key: 'intake',      href: '/my-intake',          label: 'Intake form', icon: ClipboardList },
      // 'Preferences' removed 2026-08-27 — the intake owns
      // pressure/focus/avoid now (profile redesign).
      { key: 'settings',    href: '/client/settings',    label: 'Settings',    icon: SettingsIcon },
    ],
  },
];

const ClientShell = ({ active, children }) => {
  const { user, setUser } = useContext(AuthContext);

  // Routes like /book and /availability are reachable by both clients
  // and providers (a provider booking on behalf of a client lands on
  // /book too). The desktop sidebar is a client-side surface — for
  // non-clients render children only so a provider doesn't see the
  // client menu chrome on a shared route.
  if (user?.accountType && user.accountType !== 'CLIENT') {
    return <>{children}</>;
  }

  // Initials from full name (or first letter of email as last resort).
  const initials = (() => {
    const name = user?.profile?.fullName || user?.email || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (parts[0]?.[0] || '?').toUpperCase();
  })();

  // Logout mirrors Header.js / ProviderShell exactly so behavior is
  // identical from any entry point.
  const handleLogout = async () => {
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        setUser(null);
        window.location.replace('/login');
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      {/* Fixed left sidebar — desktop only. At <lg the existing
          Header + MobileMenu render the mobile chrome. */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-paper-elev border-r border-line flex-col z-30">
        <Link to="/my-bookings" className="flex items-center px-5 pt-5 pb-5">
          <img src="/imgs/avayble_logo.png" alt="Avayble" className="h-10 w-auto" />
        </Link>

        <nav className="flex-1 overflow-y-auto px-3 pt-1 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi} className={gi === 0 ? '' : 'mt-4'}>
              {group.label && (
                <div className="font-mono text-[9.5px] text-ink-3 tracking-[0.18em] uppercase px-2.5 pb-1.5 opacity-85">
                  {group.label}
                </div>
              )}
              {group.items.map(item => {
                const Icon = item.icon;
                const isActive = active === item.key;
                return (
                  <Link
                    key={item.key}
                    to={item.href}
                    className={`relative flex items-center gap-3 px-2.5 py-2 rounded-btn text-sm transition-colors ${
                      isActive
                        ? 'bg-accent-soft text-accent-ink font-semibold'
                        : 'text-ink-2 hover:bg-paper-deep hover:text-ink font-medium'
                    }`}
                  >
                    {isActive && (
                      <span
                        className="absolute -left-3 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-accent rounded-r"
                        aria-hidden="true"
                      />
                    )}
                    <Icon className="w-[17px] h-[17px] flex-shrink-0" strokeWidth={1.75} />
                    <span className="flex-1">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Footer — avatar + name/email + a single Logout button.
            Profile lives in the main nav already, so no Settings icon
            duplicates an entry point. Logout is the destructive
            affordance, treated in clay. */}
        <div className="border-t border-line px-4 py-3.5 flex items-center gap-3">
          <div className="w-[38px] h-[38px] rounded-full bg-paper-deep border border-line grid place-items-center font-display font-semibold text-[1.05rem] text-accent-ink flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-ink truncate">
              {user?.profile?.fullName || user?.email}
            </div>
            <div className="text-[0.72rem] text-ink-3 truncate">Client</div>
          </div>
          <NotificationsBell className="w-8 h-8 !p-0 grid place-items-center border border-line text-ink-2 hover:bg-paper-deep hover:text-ink flex-shrink-0" />
          <button
            type="button"
            onClick={handleLogout}
            aria-label="Log out"
            title="Log out"
            className="w-8 h-8 rounded-btn border border-line grid place-items-center transition-colors flex-shrink-0"
            style={{ color: 'var(--r-off-ink)' }}
            onMouseOver={(e) => (e.currentTarget.style.background = 'var(--r-off-soft)')}
            onMouseOut={(e) => (e.currentTarget.style.background = '')}
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      </aside>

      {/* Main column — offset by the sidebar width on desktop. */}
      <div className="lg:ml-64 lg:min-h-screen lg:flex lg:flex-col">
        <main className="lg:flex-1 lg:px-9 lg:pt-8 lg:pb-16 lg:mx-auto lg:max-w-[1480px] w-full">
          {children}
        </main>
      </div>
    </>
  );
};

export default ClientShell;
